import { useEffect } from "react";

interface JsonLdProps {
  data: Record<string, unknown> | Record<string, unknown>[];
}

const JsonLd = ({ data }: JsonLdProps) => {
  const jsonString = JSON.stringify(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonString }}
    />
  );
};

// ── Organization + WebSite (Homepage) ──
export const OrganizationJsonLd = () => (
  <JsonLd
    data={[
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Raw Response Labs",
        legalName: "Raw Response Labs UG (haftungsbeschränkt)",
        url: "https://rawresponselabs.com",
        logo: "https://rawresponselabs.com/favicon.svg",
        description:
          "Raw Response Labs builds autonomous companies: own agentic products like webblitz.ai, and the same architecture for client companies, with a human approval step.",
        foundingDate: "2024",
        founder: {
          "@type": "Person",
          name: "Levent Valente",
          url: "https://valente.ventures",
          sameAs: ["https://linkedin.com/in/levo"],
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "Urbanstr. 71",
          addressLocality: "Berlin",
          postalCode: "10967",
          addressCountry: "DE",
        },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+49-1520-8544-838",
          email: "hello@valente.ventures",
          contactType: "sales",
        },
        sameAs: [
          "https://linkedin.com/in/levo",
          "https://webblitz.ai",
          "https://valente.ventures",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Raw Response Labs",
        url: "https://rawresponselabs.com",
        description:
          "Monitor, optimize, and orchestrate how your brand appears across ChatGPT, Claude, Gemini, Perplexity and every AI that matters.",
      },
    ]}
  />
);

// ── Person (About page — E-E-A-T authority) ──
export const FounderJsonLd = () => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "Person",
      name: "Levent Valente",
      jobTitle: "Founder & CEO",
      url: "https://valente.ventures",
      image: "https://rawresponselabs.com/assets/levent.jpg",
      sameAs: [
        "https://linkedin.com/in/levo",
        "https://valente.ventures",
      ],
      worksFor: {
        "@type": "Organization",
        name: "Raw Response Labs",
        url: "https://rawresponselabs.com",
      },
      knowsAbout: [
        "AI Visibility",
        "Generative Engine Optimization",
        "Brand Monitoring",
        "AI Search",
        "SaaS",
      ],
    }}
  />
);

// ── FAQPage (Resources page — rich results) ──
export const FaqJsonLd = ({
  categories,
}: {
  categories: { category: string; items: { question: string; answer: string }[] }[];
}) => {
  const allItems = categories.flatMap((cat) =>
    cat.items.map((item) => ({
      "@type": "Question" as const,
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer" as const,
        text: item.answer,
      },
    }))
  );

  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: allItems,
      }}
    />
  );
};

// ── ContactPage ──
export const ContactPageJsonLd = () => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "Contact Raw Response Labs",
      url: "https://rawresponselabs.com/contact",
      description:
        "Get in touch with Raw Response Labs to discuss AI visibility monitoring for your brand.",
      mainEntity: {
        "@type": "Organization",
        name: "Raw Response Labs",
        url: "https://rawresponselabs.com",
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+49-1520-8544-838",
          email: "hello@valente.ventures",
          contactType: "sales",
        },
      },
    }}
  />
);

// ── SoftwareApplication (Feature/Platform pages) ──
export const SoftwareAppJsonLd = ({
  name,
  description,
  slug,
}: {
  name: string;
  description: string;
  slug: string;
}) => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `Raw Response Labs — ${name}`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: `https://rawresponselabs.com/platform/${slug}`,
      description,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        availability: "https://schema.org/OnlineOnly",
      },
      creator: {
        "@type": "Organization",
        name: "Raw Response Labs",
        url: "https://rawresponselabs.com",
      },
    }}
  />
);

// ── Service (Solution pages) ──
export const ServiceJsonLd = ({
  name,
  description,
  slug,
}: {
  name: string;
  description: string;
  slug: string;
}) => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "Service",
      name: `Raw Response Labs - ${name}`,
      url: `https://rawresponselabs.com/solutions/${slug}`,
      description,
      provider: {
        "@type": "Organization",
        name: "Raw Response Labs",
        url: "https://rawresponselabs.com",
      },
      serviceType: "AI Visibility Monitoring",
      areaServed: "Worldwide",
    }}
  />
);

// ── Article (Resource guide pages) ──
export const ArticleJsonLd = ({
  title,
  description,
  slug,
  datePublished,
  dateModified,
  language,
}: {
  title: string;
  description: string;
  slug: string;
  datePublished?: string;
  dateModified?: string;
  language?: string;
}) => (
  <JsonLd
    data={{
      "@context": "https://schema.org",
      "@type": "Article",
      headline: title,
      url: `https://rawresponselabs.com/resources/${slug}`,
      description,
      ...(datePublished ? { datePublished } : {}),
      ...(dateModified ? { dateModified } : {}),
      ...(language ? { inLanguage: language } : {}),
      mainEntityOfPage: `https://rawresponselabs.com/resources/${slug}`,
      author: {
        "@type": "Person",
        name: "Levent Valente",
        url: "https://valente.ventures",
      },
      publisher: {
        "@type": "Organization",
        name: "Raw Response Labs",
        url: "https://rawresponselabs.com",
      },
    }}
  />
);

export default JsonLd;
